// Format auto-detection — fast, dependency-free, pure. Returns a rich type
// (json/yaml/csv/tsv/text); formatToTab() collapses it to one of the three
// V1 tabs. Full/robust parsing lives in the per-format phases; this only has
// to be good enough to pick the right tab, and the user can always override.

const DELIMITERS = [
  [',', 'csv'],
  ['\t', 'tsv'],
  [';', 'csv'],
];

/** @returns {'json'|'yaml'|'csv'|'tsv'|'text'} */
export function detectFormat(text) {
  const t = (text || '').trim();
  if (!t) return 'text';

  // JSON: only claim it if it actually parses.
  if (t[0] === '{' || t[0] === '[') {
    try {
      JSON.parse(t);
      return 'json';
    } catch {
      /* not strict JSON — try the other formats */
    }
  }

  const delimited = detectDelimited(t);
  if (delimited) return delimited;

  if (looksLikeYaml(t)) return 'yaml';

  return 'text';
}

/** Map a detected format to the V1 tab that handles it. */
export function formatToTab(fmt) {
  if (fmt === 'json' || fmt === 'yaml') return 'json';
  if (fmt === 'csv' || fmt === 'tsv') return 'csv';
  return 'text';
}

function detectDelimited(t) {
  const sample = logicalRecords(t, 20);
  if (!sample || sample.length < 2) return null;
  for (const [delim, name] of DELIMITERS) {
    const cols = countFields(sample[0], delim);
    if (cols < 2) continue;
    if (sample.every((l) => countFields(l, delim) === cols)) return name;
  }
  return null;
}

// Split physical lines into RFC-4180 logical records. Newlines inside a quoted
// field belong to the same record; escaped double-quotes do not close the field.
// Returning null for an unterminated quote keeps auto-detection conservative.
function logicalRecords(t, limit) {
  const records = [];
  let start = 0;
  let inQuotes = false;
  for (let i = 0; i < t.length; i++) {
    if (t[i] === '"') {
      if (inQuotes && t[i + 1] === '"') i++;
      else inQuotes = !inQuotes;
    } else if (t[i] === '\n' && !inQuotes) {
      const record = t.slice(start, i).replace(/\r$/, '');
      if (record.trim()) records.push(record);
      if (records.length >= limit) return records;
      start = i + 1;
    }
  }
  if (inQuotes) return null;
  const record = t.slice(start).replace(/\r$/, '');
  if (record.trim()) records.push(record);
  return records;
}

// Count delimiter-separated fields, ignoring delimiters INSIDE quoted fields
// (RFC-4180). A naive split on the delimiter miscounts `1,"Smith, John"` as three
// columns, so a real CSV export with quoted commas would fall back to text.
function countFields(line, delim) {
  let count = 1;
  let inQuotes = false;
  let separators = 0;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') i++; // escaped "" inside a quoted field
      else inQuotes = !inQuotes;
    } else if (ch === delim && !inQuotes) {
      // A lone terminal semicolon is overwhelmingly likely to be source-code or
      // CSS punctuation, not an empty second CSV field. Keep genuine semicolon
      // records, including a trailing empty third+ field after an interior `;`.
      if (delim === ';' && separators === 0 && !line.slice(i + 1).trim()) continue;
      count++;
      separators++;
    }
  }
  return count;
}

function looksLikeYaml(t) {
  const lines = t.split(/\r?\n/);
  if (/^---\s*$/.test(lines[0])) return true;
  const meaningful = lines.filter((l) => l.trim() && !l.trim().startsWith('#'));
  if (!meaningful.length) return false;
  // CSS declarations can satisfy the loose `key: value` YAML shape. When every
  // line is semicolon-terminated, keep it as source text; genuine semicolon CSV
  // has already been accepted by detectDelimited above.
  if (meaningful.every((line) => /;\s*$/.test(line))) return false;
  if (hasBlockScalar(lines)) return true;
  const yamlish = meaningful.filter(
    (l) => /^\s*-\s+\S/.test(l) || /^\s*[\w.$/-]+\s*:(\s|$)/.test(l),
  ).length;
  return yamlish / meaningful.length >= 0.7;
}

function hasBlockScalar(lines) {
  for (let i = 0; i < lines.length; i++) {
    const header = lines[i].match(/^(\s*)(?:-\s+)?[\w.$/-]+\s*:\s*[|>][+-]?\s*(?:#.*)?$/);
    if (!header) continue;
    const indent = header[1].length;
    for (let j = i + 1; j < lines.length; j++) {
      if (!lines[j].trim()) continue;
      if ((lines[j].match(/^\s*/) || [''])[0].length > indent) return true;
      break;
    }
  }
  return false;
}
